/**
 * Geometry utilities - Pure math functions for Patchwerk
 * 
 * All functions are pure: input coords → output coords/booleans
 * No DOM access, no state mutation.
 */

// ============= POINT & LINE PRIMITIVES =============

/**
 * Check if a point is inside a bounding box
 * @param {number} x - Point X coordinate
 * @param {number} y - Point Y coordinate  
 * @param {{left: number, top: number, right: number, bottom: number}} boxRect - Bounding box
 * @returns {boolean}
 */
export function pointInBox(x, y, boxRect) {
    return x >= boxRect.left && x <= boxRect.right && 
           y >= boxRect.top && y <= boxRect.bottom;
}

/**
 * Check if two line segments intersect
 * Uses parametric line intersection formula
 * @param {number} x1 - Line 1 start X
 * @param {number} y1 - Line 1 start Y
 * @param {number} x2 - Line 1 end X
 * @param {number} y2 - Line 1 end Y
 * @param {number} x3 - Line 2 start X
 * @param {number} y3 - Line 2 start Y
 * @param {number} x4 - Line 2 end X
 * @param {number} y4 - Line 2 end Y
 * @returns {boolean}
 */
export function lineIntersectsLine(x1, y1, x2, y2, x3, y3, x4, y4) {
    const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    if (Math.abs(denom) < 0.0001) return false; // Parallel lines
    
    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;
    
    return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

/**
 * Check if a line segment intersects a bounding box
 * @param {number} x1 - Line start X
 * @param {number} y1 - Line start Y
 * @param {number} x2 - Line end X
 * @param {number} y2 - Line end Y
 * @param {{left: number, top: number, right: number, bottom: number}} boxRect - Bounding box
 * @returns {boolean}
 */
export function lineIntersectsBox(x1, y1, x2, y2, boxRect) {
    // First check if either endpoint is inside the box
    if (pointInBox(x1, y1, boxRect) || pointInBox(x2, y2, boxRect)) {
        return true;
    }
    
    // Check if line intersects any of the 4 box edges
    const { left, top, right, bottom } = boxRect;
    
    if (lineIntersectsLine(x1, y1, x2, y2, left, top, right, top)) return true;       // Top edge
    if (lineIntersectsLine(x1, y1, x2, y2, left, bottom, right, bottom)) return true; // Bottom edge
    if (lineIntersectsLine(x1, y1, x2, y2, left, top, left, bottom)) return true;     // Left edge
    if (lineIntersectsLine(x1, y1, x2, y2, right, top, right, bottom)) return true;   // Right edge
    
    return false;
}

// ============= RECTANGLE OPERATIONS =============

/**
 * Check if two rectangles intersect (overlap)
 * @param {{left: number, top: number, right: number, bottom: number}} rect1 
 * @param {{left: number, top: number, right: number, bottom: number}} rect2 
 * @returns {boolean}
 */
export function rectsIntersect(rect1, rect2) {
    return !(rect1.right < rect2.left || 
             rect1.left > rect2.right || 
             rect1.bottom < rect2.top || 
             rect1.top > rect2.bottom);
}

/**
 * Expand a bounding box to include another box
 * @param {{left: number, top: number, right: number, bottom: number}|null} bounds - Current bounds (null for initial)
 * @param {{left: number, top: number, right: number, bottom: number}} box - Box to include
 * @returns {{left: number, top: number, right: number, bottom: number}}
 */
export function expandBounds(bounds, box) {
    if (!bounds) {
        return { ...box };
    }
    return {
        left: Math.min(bounds.left, box.left),
        top: Math.min(bounds.top, box.top),
        right: Math.max(bounds.right, box.right),
        bottom: Math.max(bounds.bottom, box.bottom)
    };
}

// ============= BOUNDING BOX CALCULATIONS =============

/**
 * Calculate bounding box from an array of devices with positions
 * @param {Array<{position: {x: number, y: number}}>} devices - Array of device objects
 * @param {number} defaultWidth - Default device width estimate (used when dimensions unknown)
 * @param {number} defaultHeight - Default device height estimate
 * @returns {{minX: number, minY: number, maxX: number, maxY: number, centerX: number, centerY: number}}
 */
export function getSelectionBounds(devices, defaultWidth = 200, defaultHeight = 150) {
    if (!devices || devices.length === 0) {
        return { minX: 0, minY: 0, maxX: 0, maxY: 0, centerX: 0, centerY: 0 };
    }
    
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    devices.forEach(d => {
        minX = Math.min(minX, d.position.x);
        minY = Math.min(minY, d.position.y);
        // Use actual dimensions if available, otherwise estimate
        const width = d.width || defaultWidth;
        const height = d.height || defaultHeight;
        maxX = Math.max(maxX, d.position.x + width);
        maxY = Math.max(maxY, d.position.y + height);
    });
    
    return {
        minX, minY, maxX, maxY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2
    };
}

// ============= DIRECTION HELPERS =============

/**
 * Get direction vector for a port side
 * @param {'top'|'bottom'|'left'|'right'} side - Port side
 * @returns {{dx: number, dy: number}} - Unit direction vector
 */
export function getDirectionVector(side) {
    switch(side) {
        case 'top': return { dx: 0, dy: -1 };
        case 'bottom': return { dx: 0, dy: 1 };
        case 'left': return { dx: -1, dy: 0 };
        case 'right': 
        default: return { dx: 1, dy: 0 };
    }
}

// ============= BEZIER CURVE MATH =============

/**
 * Calculate a point along a cubic bezier curve
 * @param {number} t - Parameter (0 to 1)
 * @param {number} p0x - Start point X
 * @param {number} p0y - Start point Y
 * @param {number} cp1x - Control point 1 X
 * @param {number} cp1y - Control point 1 Y
 * @param {number} cp2x - Control point 2 X
 * @param {number} cp2y - Control point 2 Y
 * @param {number} p3x - End point X
 * @param {number} p3y - End point Y
 * @returns {{x: number, y: number}}
 */
export function bezierPoint(t, p0x, p0y, cp1x, cp1y, cp2x, cp2y, p3x, p3y) {
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    
    return {
        x: mt3 * p0x + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * p3x,
        y: mt3 * p0y + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * p3y
    };
}

/**
 * Calculate bezier control points for a cable path
 * Uses direction vectors and distance-based tension
 * @param {number} p1x - Start of curve X (after straight segment)
 * @param {number} p1y - Start of curve Y
 * @param {number} p2x - End of curve X (before straight segment)
 * @param {number} p2y - End of curve Y
 * @param {{dx: number, dy: number}} dir1 - Direction at start
 * @param {{dx: number, dy: number}} dir2 - Direction at end
 * @param {number} minTension - Minimum tension value (default 30)
 * @param {number} maxTension - Maximum tension value (default 120)
 * @param {number} tensionFactor - Factor of distance to use for tension (default 0.5)
 * @returns {{cp1x: number, cp1y: number, cp2x: number, cp2y: number}}
 */
export function calculateControlPoints(p1x, p1y, p2x, p2y, dir1, dir2, minTension = 30, maxTension = 120, tensionFactor = 0.5) {
    const dist = Math.hypot(p2x - p1x, p2y - p1y);
    const tension = Math.max(minTension, Math.min(dist * tensionFactor, maxTension));
    
    return {
        cp1x: p1x + dir1.dx * tension,
        cp1y: p1y + dir1.dy * tension,
        cp2x: p2x + dir2.dx * tension,
        cp2y: p2y + dir2.dy * tension
    };
}

/**
 * Sample points along a bezier curve
 * @param {number} p0x - Start point X
 * @param {number} p0y - Start point Y
 * @param {number} cp1x - Control point 1 X
 * @param {number} cp1y - Control point 1 Y
 * @param {number} cp2x - Control point 2 X
 * @param {number} cp2y - Control point 2 Y
 * @param {number} p3x - End point X
 * @param {number} p3y - End point Y
 * @param {number} samples - Number of samples (default 20)
 * @returns {Array<{x: number, y: number}>} - Array of points along curve
 */
export function sampleBezierCurve(p0x, p0y, cp1x, cp1y, cp2x, cp2y, p3x, p3y, samples = 20) {
    const points = [];
    for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        points.push(bezierPoint(t, p0x, p0y, cp1x, cp1y, cp2x, cp2y, p3x, p3y));
    }
    return points;
}

// ============= CABLE INTERSECTION =============

/**
 * Check if a cable path intersects a bounding box
 * This handles the full cable geometry: straight segments + bezier curve
 * @param {number} x1 - Source port X
 * @param {number} y1 - Source port Y
 * @param {'top'|'bottom'|'left'|'right'} side1 - Source port side
 * @param {number} x2 - Target port X
 * @param {number} y2 - Target port Y
 * @param {'top'|'bottom'|'left'|'right'} side2 - Target port side
 * @param {{left: number, top: number, right: number, bottom: number}} boxRect - Bounding box
 * @param {number} straightDist - Distance for straight segment before curve (default 10)
 * @param {number} samples - Number of bezier samples (default 20)
 * @returns {boolean}
 */
export function cableIntersectsBox(x1, y1, side1, x2, y2, side2, boxRect, straightDist = 10, samples = 20) {
    const dir1 = getDirectionVector(side1);
    const dir2 = getDirectionVector(side2);
    
    // End of straight segments
    const p1x = x1 + dir1.dx * straightDist;
    const p1y = y1 + dir1.dy * straightDist;
    const p2x = x2 + dir2.dx * straightDist;
    const p2y = y2 + dir2.dy * straightDist;
    
    // Calculate bezier control points
    const { cp1x, cp1y, cp2x, cp2y } = calculateControlPoints(p1x, p1y, p2x, p2y, dir1, dir2);
    
    // Check start and end straight segments
    if (lineIntersectsBox(x1, y1, p1x, p1y, boxRect)) return true;
    if (lineIntersectsBox(p2x, p2y, x2, y2, boxRect)) return true;
    
    // Sample points along the bezier curve and check each segment
    let prevX = p1x, prevY = p1y;
    
    for (let i = 1; i <= samples; i++) {
        const t = i / samples;
        const { x: px, y: py } = bezierPoint(t, p1x, p1y, cp1x, cp1y, cp2x, cp2y, p2x, p2y);
        
        if (lineIntersectsBox(prevX, prevY, px, py, boxRect)) return true;
        
        prevX = px;
        prevY = py;
    }
    
    return false;
}
